import { Button } from '@/components/Button';
import { Image } from '@/components/Image';
import { ImageCropper } from '@/components/ImageCropper';
import { Text } from '@/components/Text';
import { useEventStore } from '@/stores/useEventStore';
import { handleError } from '@/utils/handleError';
import { memo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';

export const UpdateEventGeneralCover = memo(() => {
  const { t } = useTranslation();
  const { id } = useParams();
  const [imageSrcForCrop, setImageSrcForCrop] = useState<string>('');
  const [imageCropperIsOpen, setImageCropperIsOpen] = useState<boolean>(false);
  const { event, uploadEventCover, removeEventCover } = useEventStore();

  if (!event || !id) return null;

  const handleUpdateCover = () => {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = 'image/*';
    input.onchange = async () => {
      const file = input.files?.[0];
      if (file) {
        setImageSrcForCrop(URL.createObjectURL(file));
        setImageCropperIsOpen(true);
      }
    };
    input.click();
  };

  const handleRemoveCover = async () => {
    await removeEventCover(Number(id));
  };

  const handleCropComplete = async (croppedImage: File) => {
    setImageCropperIsOpen(false);
    try {
      const reader = new FileReader();
      reader.onload = () => {
        if (typeof reader.result === 'string') {
          uploadEventCover(Number(id), reader.result);
        }
      };
      reader.readAsDataURL(croppedImage);
    } catch (error) {
      handleError(error);
    }
  };

  return (
    <div className="flex flex-col items-center justify-center md:items-start">
      <Text className="text-base font-medium text-sky-800">
        {t('updateEvent:general.cover.title')}
      </Text>
      <div className="mt-4 flex w-full flex-col items-center justify-center">
        <Image
          className="aspect-video w-full rounded object-cover shadow-sm"
          src={event.cover}
          alt={event.name}
          refreshKey={Date.now()}
        />
        <div className="mt-4 flex w-full flex-col space-y-3 md:flex-row md:space-x-3 md:space-y-0">
          <Button className="w-full lg:w-auto" onClick={handleUpdateCover}>
            {event.cover
              ? t('updateEvent:general.cover.update')
              : t('updateEvent:general.cover.add')}
          </Button>
          {event.cover && (
            <Button
              className="w-full bg-danger lg:w-auto"
              onClick={handleRemoveCover}
            >
              {t('updateEvent:general.cover.remove')}
            </Button>
          )}
        </div>
      </div>
      {imageCropperIsOpen && (
        <ImageCropper
          cropShape="rect"
          showGrid={false}
          ratio={16 / 9}
          imageSrc={imageSrcForCrop}
          isOpen={imageCropperIsOpen}
          onClose={() => setImageCropperIsOpen(false)}
          onCropIsComplete={handleCropComplete}
        />
      )}
    </div>
  );
});
